import {TOperator, and, any, not, or} from '@edna/utils';

import {EPermission, EResellerIds, ERoles} from 'src/containers/User';
import PATHS from 'src/paths';

import {bootstrapComponents} from './utils';

/* prettier-ignore */
const componentImports = {
  Home: () => import(/* webpackChunkName: "channels" */ '../Home'),
  NotFound: () => import(/* webpackChunkName: "not-found" */ '../NotFound'),
  ServiceUnavailable: () =>
    import(/* webpackChunkName: "service-unavailable" */ '../ServiceUnavailable'),
  // Auth
  SignIn: () => import(/* webpackChunkName: "sign-in" */ '../Auth/SignIn'),
  SignUp: () => import(/* webpackChunkName: "sign-up" */ '../SignUp'),
  PasswordSaved: () => import(/* webpackChunkName: "password-saved" */ '../Auth/PasswordSaved'),
  EmailConfirm: () => import(/* webpackChunkName: "email-confirm" */ '../Auth/EmailConfirm'),
  SignInByInviteLink: () =>
    import(/* webpackChunkName: "sign-up-by-invite-link" */ '../SignInByInviteLink'),
  InvoiceCancel: () => import(/* webpackChunkName: "invoice-cancel" */ '../Invoices/CancelForm'),
  InvoiceAssign: () => import(/* webpackChunkName: "invoice-assign" */ '../Invoices/AssignForm'),

  // Channels
  Channels: () => import(/* webpackChunkName: "channels" */ '../Channels'),
  ChannelsVerificationForm: () =>
    import(
      /* webpackChunkName: "channels-verification-form" */ '../Channels/Verification/EditForm'
    ),
  WhatsAppChannel: () =>
    import(/* webpackChunkName: "whats-app-channel-form" */ '../WhatsAppChannel/ViewForm'),
  WhatsAppChannelForm: () =>
    import(/* webpackChunkName: "whats-app-channel-form" */ '../WhatsAppChannel/EditForm'),
  WhatsAppTechPartnerChannelForm: () =>
    import(
      /* webpackChunkName: "whats-app-tech-partner-channel-form" */ '../WhatsAppChannel/TechPartnerForm'
    ),
  WhatsAppBusinessAppChannelForm: () =>
    import(
      /* webpackChunkName: "whats-app-business-app-channel-form" */ '../WhatsAppChannel/BusinessAppForm'
    ),
  WhatsAppPublicChannelForm: () =>
    import(/* webpackChunkName: "whats-app-public-channel-form" */ '../WhatsAppChannel/PublicForm'),
  ViberChannelEditForm: () =>
    import(/* webpackChunkName: "viber-channel-edit-form" */ '../ViberChannel/EditForm'),
  ViberChannelViewForm: () =>
    import(/* webpackChunkName: "viber-channel-view-form" */ '../ViberChannel/ViewForm'),
  SmsChannelEdit: () => import(/* webpackChunkName: "sms-channel-edit" */ '../SmsChannel/EditForm'),
  PushAppData: () => import(/* webpackChunkName: "push-app-data" */ '../PushApps/AppData'),
  PushChannelApps: () => import(/* webpackChunkName: "push-channel-apps" */ '../PushApps'),
  PushChannelEdit: () =>
    import(/* webpackChunkName: "push-channel-edit" */ '../PushChannel/EditForm'),
  StartSetupApp: () =>
    import(/* webpackChunkName: "start-setup-app" */ '../PushApps/EditForm/StartSetupApp'),
  PushAppsIOSEdit: () =>
    import(/* webpackChunkName: "push-apps-ios-edit" */ '../PushApps/EditForm/IOS'),
  PushAppsAndroidEdit: () =>
    import(/* webpackChunkName: "push-apps-android-edit" */ '../PushApps/EditForm/Android'),
  VkNotifyEdit: () =>
    import(/* webpackChunkName: "vk-notify-channel-edit" */ '../VkNotifyChannel/EditForm'),
  OkNotifyEdit: () =>
    import(/* webpackChunkName: "ok-notify-channel-edit" */ '../OkNotifyChannel/EditForm'),
  MaxBotChannelEdit: () =>
    import(/* webpackChunkName: "max-bot-channel-edit" */ '../MaxBotChannel/EditForm'),
  MaxChannelEdit: () => import(/* webpackChunkName: "max-channel-edit" */ '../MaxChannel/EditForm'),
  MaxChannelView: () => import(/* webpackChunkName: "max-channel-view" */ '../MaxChannel/ViewForm'),
  MaxTechPartnerChannelForm: () =>
    import(/* webpackChunkName: "max-tech-partner-channel-form" */ '../MaxChannel/TechPartnerForm'),
  MaxPublicChannelForm: () =>
    import(/* webpackChunkName: "max-public-channel-form" */ '../MaxChannel/PublicForm'),
  TelegramChannelEdit: () =>
    import(/* webpackChunkName: "telegram-channel-edit" */ '../TelegramChannel/EditForm'),
  TelegramChannelView: () =>
    import(/* webpackChunkName: "telegram-channel-view" */ '../TelegramChannel/ViewForm'),
  TelegramTechPartnerChannelForm: () =>
    import(
      /* webpackChunkName: "telegram-tech-partner-channel-form" */ '../TelegramChannel/TechPartnerForm'
    ),
  TelegramPublicChannelForm: () =>
    import(/* webpackChunkName: "telegram-public-channel-form" */ '../TelegramChannel/PublicForm'),

  // Broadcasts
  Broadcasts: () => import(/* webpackChunkName: "broadcasts" */ '../Broadcasts'),
  BroadcastDetails: () =>
    import(/* webpackChunkName: "broadcast-details" */ '../Broadcasts/Details'),
  BroadcastsEdit: () => import(/* webpackChunkName: "broadcasts-edit" */ '../Broadcasts/Edit'),
  // Subscribers
  Subscribers: () => import(/* webpackChunkName: "subscribers" */ '../Subscribers'),
  SubscribersEdit: () =>
    import(/* webpackChunkName: "subscribers-edit" */ '../Subscribers/EditForm'),
  SubscribersGroupEdit: () =>
    import(/* webpackChunkName: "subscribers-group-edit" */ '../Subscribers/GroupEditForm'),
  // MessageMatchers
  MessageMatchers: () => import(/* webpackChunkName: "message-matchers" */ '../MessageMatchers'),
  WhatsAppMessageMatcherEdit: () =>
    import(
      /* webpackChunkName: "new-whats-app-message-matcher-edit" */ '../MessageMatchers/WhatsAppForm'
    ),
  WhatsAppMessageMatcherView: () =>
    import(
      /* webpackChunkName: "new-whats-app-message-matcher-view" */ '../MessageMatchers/WhatsAppForm/WhatsAppView'
    ),
  ViberMessageMatcherEdit: () =>
    import(/* webpackChunkName: "viber-message-matcher-edit" */ '../MessageMatchers/ViberForm'),
  SmsMessageMatcherEdit: () =>
    import(/* webpackChunkName: "sms-message-matcher-edit" */ '../MessageMatchers/SMSForm'),
  SmsMessageMatcherView: () =>
    import(/* webpackChunkName: "sms-message-matcher-view" */ '../MessageMatchers/SMSForm/View'),
  OkNotifyMessageMatcherEdit: () =>
    import(
      /* webpackChunkName: "ok-notify-message-matcher-edit" */ '../MessageMatchers/OkNotifyForm'
    ),
  VkNotifyMessageMatcherEdit: () =>
    import(
      /* webpackChunkName: "vk-notify-message-matcher-edit" */ '../MessageMatchers/VkNotifyForm'
    ),
  // Cascades
  Cascades: () => import(/* webpackChunkName: "cascades" */ '../Cascades'),
  CascadeEdit: () => import(/* webpackChunkName: "cascade-edit" */ '../Cascades/EditForm'),
  Profile: () => import(/* webpackChunkName: "profile" */ '../Profile'),
  // Company
  UserCompanyProfile: () =>
    import(/* webpackChunkName: "user-company-profile" */ '../UserCompanyProfile'),
  CompanyNews: () => import(/* webpackChunkName: "company-news" */ '../CompanyNews'),
  LeadCompanyClients: () =>
    import(/* webpackChunkName: "lead-company-clients" */ '../LeadCompanyClients/List'),
  LeadCompanyClientCard: () =>
    import(
      /* webpackChunkName: "lead-company-client-card" */ '../LeadCompanyClients/LeadCompanyClientCard'
    ),
  LeadCompanyClientEdit: () =>
    import(/* webpackChunkName: "lead-client-edit" */ '../LeadCompanyClients/EditForm'),
  AgentCompanyClients: () =>
    import(/* webpackChunkName: "agent-company-clients" */ '../AgentCompanyClients/List'),
  AgentCompanyClientCard: () =>
    import(/* webpackChunkName: "agent-company-client-card" */ '../AgentViewCard'),
  AgentCompanyClientEdit: () =>
    import(/* webpackChunkName: "agent-company-client-edit" */ '../AgentCompanyClients/CreateForm'),
  // Support
  SupportTabs: () => import(/* webpackChunkName: "support-tabs" */ '../SupportTabs'),
  SupportVerificationsForm: () =>
    import(/* webpackChunkName: "support-verifications-view" */ '../SupportVerifications/ViewForm'),
  SupportClientCard: () =>
    import(/* webpackChunkName: "support-client-card" */ '../SupportClients/SupportClientCard'),
  // Integration
  Integration: () => import(/* webpackChunkName: "integration" */ '../Integration'),
  IntegrationApiKey: () =>
    import(
      /* webpackChunkName: "integration-api-key" */ '../IntegrationSettings/ApiKeySettings/EditForm'
    ),
  IntegrationCallback: () =>
    import(
      /* webpackChunkName: "integration-callback" */ '../IntegrationSettings/CallbackSettings/EditForm'
    ),
  // Corrections
  Corrections: () => import(/* webpackChunkName: "corrections" */ '../Corrections'),
  // Analytics
  Report: () => import(/* webpackChunkName: "report" */ '../Report'),
  SummaryReport: () => import(/* webpackChunkName: "summary-report" */ '../SummaryReport'),
  ChannelReport: () =>
    import(/* webpackChunkName: "channel-report" */ '../SummaryReport/ChannelReport'),
  MessageMatcherReportList: () =>
    import(/* webpackChunkName: "message-matcher-report-list" */ '../MessageMatcherReports'),
  MessageMatcherReport: () =>
    import(
      /* webpackChunkName: "message-matcher-report" */ '../MessageMatcherReports/MessageMatcherReport'
    ),
  // Tariffs
  Tariffs: () => import(/* webpackChunkName: "tariffs" */ '../TariffsCatalog/List'),
  Tariff: () => import(/* webpackChunkName: "tariff" */ '../TariffsCatalog/TariffCard'),
  TariffDetailEdit: () =>
    import(/* webpackChunkName: "tariff-detail-edit" */ '../TariffsCatalog/TariffDetailEdit'),
  TariffPricingDetailEdit: () =>
    import(
      /* webpackChunkName: "tariff-pricing-detail-edit" */ '../TariffsCatalog/TariffPricingDetailEdit'
    ),

  Dialogs: () => import(/* webpackChunkName: "dialogs" */ '../Dialogs'),
  NotificationsHistory: () =>
    import(/* webpackChunkName: "notifications-history" */ '../NotificationsHistory'),
  Help: () => import(/* webpackChunkName: "help" */ '../Help'),
  Triggers: () => import(/* webpackChunkName: "triggers" */ '../Triggers'),
  TriggersEdit: () => import(/* webpackChunkName: "triggers-edit" */ '../Triggers/EditForm'),
  Files: () => import(/* webpackChunkName: "files" */ '../ServerFile/Files'),

  // Support views for client pages
  SupportMessageMatchers: () =>
    import(/* webpackChunkName: "support-message-matchers" */ '../MessageMatchers/support'),
  SupportMessageMatcherView: () =>
    import(
      /* webpackChunkName: "support-message-matcher-view" */ '../MessageMatchers/support/View'
    ),
  SupportReport: () => import(/* webpackChunkName: "report" */ '../Report'),
  SupportSummaryReport: () =>
    import(/* webpackChunkName: "support-summary-report" */ '../SummaryReport/support'),
  SupportChannelReport: () =>
    import(
      /* webpackChunkName: "support-channel-report" */ '../SummaryReport/support/ChannelReport'
    ),
  SupportChannels: () => import(/* webpackChunkName: "support-channels" */ '../Channels/support'),
  SupportChannelCard: () =>
    import(/* webpackChunkName: "support-channel-card" */ '../Channels/support/ChannelCard'),

  // Agent profile
  AgentClients: () => import(/* webpackChunkName: "agent-clients" */ '../AgentClients'),
  AgentClientCard: () =>
    import(/* webpackChunkName: "agent-client-card" */ '../AgentClients/ViewCard'),
  AgentPayments: () => import(/* webpackChunkName: "agent-payments" */ '../AgentPayments'),
  AgentProfile: () => import(/* webpackChunkName: "agent-profile" */ '../AgentViewCard'),
};

/* prettier-ignore-end */

const C = bootstrapComponents(componentImports);

export {C as Components};

type TPrivateRoutes = Record<
  ValueOf<typeof PATHS>,
  {
    component: ValueOf<typeof C>;
    permissions: TOperator<EPermission>;
    roles: TOperator<ERoles>;
    resellers: TOperator<EResellerIds>;
  }
>;

export const PRIVATE_ROUTES: TPrivateRoutes = {
  [PATHS.HOME]: {
    component: C.Home,
    permissions: any(),
    roles: any(),
    resellers: any(),
  },
  [PATHS.BROADCASTS_LIST]: {
    component: C.Broadcasts,
    permissions: and<EPermission>('PERMISSION_BROADCAST_READ'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.BROADCASTS_NEW]: {
    component: C.BroadcastsEdit,
    permissions: and<EPermission>(
      'PERMISSION_BROADCAST_READ',
      'PERMISSION_BROADCAST_WRITE',
      'PERMISSION_WRITE',
    ),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.BROADCASTS_EDIT]: {
    component: C.BroadcastsEdit,
    permissions: and<EPermission>('PERMISSION_BROADCAST_READ'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.BROADCASTS_DETAILS]: {
    component: C.BroadcastDetails,
    permissions: and<EPermission>('PERMISSION_BROADCAST_READ'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS]: {
    component: C.Channels,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_NEW]: {
    component: C.WhatsAppChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_EDIT]: {
    component: C.WhatsAppChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_VIEW]: {
    component: C.WhatsAppChannel,
    permissions: and<EPermission>('PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_TECH_PARTNER_NEW]: {
    component: C.WhatsAppTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_TECH_PARTNER_EDIT]: {
    component: C.WhatsAppTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_TECH_PARTNER_VIEW]: {
    component: C.WhatsAppTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_BUSINESS_APP_NEW]: {
    component: C.WhatsAppBusinessAppChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_BUSINESS_APP_EDIT]: {
    component: C.WhatsAppBusinessAppChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_WHATSAPP_BUSINESS_APP_TECH_PARTNER_NEW]: {
    component: C.WhatsAppTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VIBER_NEW]: {
    component: C.ViberChannelEditForm,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VIBER_EDIT]: {
    component: C.ViberChannelEditForm,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_VIBER_VIEW]: {
    component: C.ViberChannelViewForm,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_SMS_NEW]: {
    component: C.SmsChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_SMS_EDIT]: {
    component: C.SmsChannelEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_SMS_VIEW]: {
    component: C.SmsChannelEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_APP_DATA]: {
    component: C.PushAppData,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_NEW]: {
    component: C.PushChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_LIST]: {
    component: C.PushChannelApps,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_IOS_EDIT]: {
    component: C.PushAppsIOSEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_APP_NEW]: {
    component: C.StartSetupApp,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_IOS_NEW_STEP]: {
    component: C.PushAppsIOSEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_ANDROID_NEW_STEP]: {
    component: C.PushAppsAndroidEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_PUSH_ANDROID_EDIT]: {
    component: C.PushAppsAndroidEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CASCADES_LIST]: {
    component: C.Cascades,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CASCADES_NEW]: {
    component: C.CascadeEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CASCADES_VIEW]: {
    component: C.CascadeEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS]: {
    component: C.MessageMatchers,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_WHATSAPP_NEW]: {
    component: C.WhatsAppMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_WHATSAPP_EDIT]: {
    component: C.WhatsAppMessageMatcherEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_WHATSAPP_VIEW]: {
    component: C.WhatsAppMessageMatcherView,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VIBER_NEW]: {
    component: C.ViberMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VIBER_EDIT]: {
    component: C.ViberMessageMatcherEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VIBER_VIEW]: {
    component: C.ViberMessageMatcherEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_SMS_NEW]: {
    component: C.SmsMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_SMS_EDIT]: {
    component: C.SmsMessageMatcherEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_SMS_VIEW]: {
    component: C.SmsMessageMatcherView,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VK_NOTIFY_NEW]: {
    component: C.VkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VK_NOTIFY_EDIT]: {
    component: C.VkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_VK_NOTIFY_VIEW]: {
    component: C.VkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.TRIGGERS_LIST]: {
    component: C.Triggers,
    permissions: and<EPermission>('PERMISSION_FLOW'),
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    resellers: any(),
  },
  [PATHS.TRIGGERS_NEW]: {
    component: C.TriggersEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    resellers: any(),
  },
  [PATHS.TRIGGERS_EDIT]: {
    component: C.TriggersEdit,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.TRIGGERS_VIEW]: {
    component: C.TriggersEdit,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_TRIGGERS_LIST]: {
    component: C.Triggers,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: and<EPermission>('PERMISSION_TENANT_FLOW_READ'),
    resellers: any(),
  },
  [PATHS.SUPPORT_TRIGGERS_VIEW]: {
    component: C.TriggersEdit,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: and<EPermission>('PERMISSION_TENANT_FLOW_READ'),
    resellers: any(),
  },
  [PATHS.SUBSCRIBERS_LIST]: {
    component: C.Subscribers,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUBSCRIBERS_NEW]: {
    component: C.SubscribersEdit,
    permissions: and<EPermission>('PERMISSION_WRITE'),
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    resellers: any(),
  },
  [PATHS.SUBSCRIBERS_EDIT]: {
    component: C.SubscribersEdit,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUBSCRIBERS_GROUP_EDIT]: {
    component: C.SubscribersGroupEdit,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.PROFILE]: {
    component: C.Profile,
    roles: any(),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.COMPANY_PROFILE]: {
    component: C.UserCompanyProfile,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.NEWS]: {
    component: C.CompanyNews,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: and<EResellerIds>(EResellerIds.OSK_ID),
  },
  [PATHS.COMPANY_CLIENTS]: {
    component: C.LeadCompanyClients,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.COMPANY_CLIENT_CARD]: {
    component: C.LeadCompanyClientCard,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.COMPANY_CLIENT_EDIT]: {
    component: C.LeadCompanyClientEdit,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.COMPANY_CLIENT_NEW]: {
    component: C.LeadCompanyClientEdit,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.COMPANY_AGENT_CLIENTS]: {
    component: C.AgentCompanyClients,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: not(or<EResellerIds>(EResellerIds.BLR_FIRST_ID, EResellerIds.BLR_SECOND_ID)),
  },
  [PATHS.COMPANY_AGENT_CLIENT_CARD]: {
    component: C.AgentCompanyClientCard,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: not(or<EResellerIds>(EResellerIds.BLR_FIRST_ID, EResellerIds.BLR_SECOND_ID)),
  },
  [PATHS.COMPANY_AGENT_CLIENT_NEW]: {
    component: C.AgentCompanyClientEdit,
    roles: and<ERoles>('ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: not(or<EResellerIds>(EResellerIds.BLR_FIRST_ID, EResellerIds.BLR_SECOND_ID)),
  },
  [PATHS.CORRECTIONS]: {
    component: C.Corrections,
    roles: or<ERoles>(
      'ROLE_TENANT_LICENSE_RENEW',
      'ROLE_TENANT_TRANSACT_STORNED',
      'ROLE_PAYKEEPER_CHANGE',
      'ROLE_SUPPORT',
    ),
    permissions: or<EPermission>(
      'PERMISSION_TENANT_LICENSE_RENEW',
      'PERMISSION_TENANT_TRANSACT_STORNED',
      'PERMISSION_PAYKEEPER_CHANGE',
      'PERMISSION_TENANT_TARIFF_WRITE',
    ),
    resellers: any(),
  },
  [PATHS.SUPPORT]: {
    component: C.SupportTabs,
    permissions: and<EPermission>('PERMISSION_TENANT_LIST_READ'),
    roles: and<ERoles>('ROLE_SUPPORT'),
    resellers: any(),
  },
  [PATHS.SUPPORT_VERIFICATIONS_VIEW]: {
    component: C.SupportVerificationsForm,
    permissions: and<EPermission>('PERMISSION_TENANT_LIST_READ'),
    roles: and<ERoles>('ROLE_SUPPORT'),
    resellers: any(),
  },
  [PATHS.SUPPORT_VERIFICATIONS_CHECK]: {
    component: C.SupportVerificationsForm,
    permissions: and<EPermission>('PERMISSION_TENANT_LIST_READ'),
    roles: and<ERoles>('ROLE_SUPPORT'),
    resellers: any(),
  },
  [PATHS.SUPPORT_CLIENT_CARD]: {
    component: C.SupportClientCard,
    permissions: and<EPermission>('PERMISSION_TENANT_DATA_READ'),
    roles: and<ERoles>('ROLE_SUPPORT'),
    resellers: any(),
  },
  [PATHS.INTEGRATION]: {
    component: C.Integration,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.INTEGRATION_SETTINGS_API_KEY_NEW]: {
    component: C.IntegrationApiKey,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.INTEGRATION_SETTINGS_CALLBACK_NEW]: {
    component: C.IntegrationCallback,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.INTEGRATION_SETTINGS_CALLBACK_EDIT]: {
    component: C.IntegrationCallback,
    roles: and<ERoles>('ROLE_USER', not<ERoles>('ROLE_BASIC_USER')),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.ANALYTICS_MESSAGES]: {
    component: C.Report,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.ANALYTICS_SUMMARY_REPORT]: {
    component: C.SummaryReport,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: and<EPermission>('PERMISSION_VIEW_BILLING'),
    resellers: any(),
  },
  [PATHS.ANALYTICS_CHANNEL]: {
    component: C.ChannelReport,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: and<EPermission>('PERMISSION_VIEW_BILLING'),
    resellers: any(),
  },
  [PATHS.ANALYTICS_MESSAGE_MATCHERS_LIST]: {
    component: C.MessageMatcherReportList,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.ANALYTICS_MESSAGE_MATCHER]: {
    component: C.MessageMatcherReport,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.NOTIFICATIONS]: {
    component: C.NotificationsHistory,
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.HELP]: {
    component: C.Help,
    roles: any(),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.FILES]: {
    component: C.Files,
    roles: any(),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.UNAVAILABLE]: {
    component: C.ServiceUnavailable,
    roles: any(),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.CHANNELS_VERIFICATION_NEW]: {
    component: C.ChannelsVerificationForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VERIFICATION_EDIT]: {
    component: C.ChannelsVerificationForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VERIFICATION_VIEW]: {
    component: C.ChannelsVerificationForm,
    permissions: and<EPermission>('PERMISSION_WHATSAPP_PROFILE'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VK_NOTIFY_NEW]: {
    component: C.VkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VK_NOTIFY_EDIT]: {
    component: C.VkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_VK_NOTIFY_VIEW]: {
    component: C.VkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_VK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_OK_NOTIFY_NEW]: {
    component: C.OkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_OK_NOTIFY_EDIT]: {
    component: C.OkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_OK_NOTIFY_VIEW]: {
    component: C.OkNotifyEdit,
    permissions: and<EPermission>('PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_BOT_NEW]: {
    component: C.MaxBotChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX_BOT'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_BOT_EDIT]: {
    component: C.MaxBotChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX_BOT'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_BOT_VIEW]: {
    component: C.MaxBotChannelEdit,
    permissions: and<EPermission>('PERMISSION_MAX_BOT'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_NEW]: {
    component: C.MaxChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_EDIT]: {
    component: C.MaxChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_VIEW]: {
    component: C.MaxChannelView,
    permissions: and<EPermission>('PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_TECH_PARTNER_NEW]: {
    component: C.MaxTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_TECH_PARTNER_EDIT]: {
    component: C.MaxTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_MAX_TECH_PARTNER_VIEW]: {
    component: C.MaxTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_MAX'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_NEW]: {
    component: C.TelegramChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_EDIT]: {
    component: C.TelegramChannelEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_VIEW]: {
    component: C.TelegramChannelView,
    permissions: and<EPermission>('PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_TECH_PARTNER_NEW]: {
    component: C.TelegramTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_TECH_PARTNER_EDIT]: {
    component: C.TelegramTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.CHANNELS_TELEGRAM_TECH_PARTNER_VIEW]: {
    component: C.TelegramTechPartnerChannelForm,
    permissions: and<EPermission>('PERMISSION_TG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_OK_NOTIFY_NEW]: {
    component: C.OkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_WRITE', 'PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_OK_NOTIFY_EDIT]: {
    component: C.OkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.MESSAGE_MATCHERS_OK_NOTIFY_VIEW]: {
    component: C.OkNotifyMessageMatcherEdit,
    permissions: and<EPermission>('PERMISSION_OK_NOTIFY'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.SUPPORT_INVOICE_ASSIGN]: {
    component: C.InvoiceAssign,
    permissions: and<EPermission>('CCB_OPERATION_CREDIT'),
    roles: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_INVOICE_CANCEL]: {
    component: C.InvoiceCancel,
    permissions: and<EPermission>('CCB_OPERATION_CANCELLATION'),
    roles: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_MESSAGE_MATCHERS_LIST]: {
    component: C.SupportMessageMatchers,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_MESSAGE_MATCHER_VIEW]: {
    component: C.SupportMessageMatcherView,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_ANALYTICS_MESSAGES]: {
    component: C.SupportReport,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_ANALYTICS_SUMMARY_REPORT]: {
    component: C.SupportSummaryReport,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_ANALYTICS_CHANNEL]: {
    component: C.SupportChannelReport,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_CHANNELS_LIST]: {
    component: C.SupportChannels,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_CHANNEL_VIEW]: {
    component: C.SupportChannelCard,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_AGENT_CLIENTS]: {
    component: C.AgentClients,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_AGENT_PAYMENTS]: {
    component: C.AgentPayments,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.SUPPORT_AGENT_CLIENT_CARD]: {
    component: C.AgentClientCard,
    roles: or<ERoles>('ROLE_SUPPORT', 'ROLE_ACCOUNT_MANAGER'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.AGENT_PAYMENTS]: {
    component: C.AgentPayments,
    roles: and<ERoles>('ROLE_AGENT'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.AGENT_CLIENTS]: {
    component: C.AgentClients,
    roles: and<ERoles>('ROLE_AGENT'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.AGENT_CLIENT_CARD]: {
    component: C.AgentClientCard,
    roles: and<ERoles>('ROLE_AGENT'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.AGENT_PROFILE]: {
    component: C.AgentProfile,
    roles: and<ERoles>('ROLE_AGENT'),
    permissions: any(),
    resellers: any(),
  },
  [PATHS.DIALOGS]: {
    component: C.Dialogs,
    permissions: and<EPermission>('PERMISSION_DIALOG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.DIALOGS_NEW]: {
    component: C.Dialogs,
    permissions: and<EPermission>('PERMISSION_DIALOG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.DIALOGS_MESSAGES]: {
    component: C.Dialogs,
    permissions: and<EPermission>('PERMISSION_DIALOG'),
    roles: or<ERoles>('ROLE_USER', 'ROLE_BASIC_USER'),
    resellers: any(),
  },
  [PATHS.TARIFFS]: {
    component: C.Tariffs,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
    resellers: any(),
  },
  [PATHS.TARIFF_CARD]: {
    component: C.Tariff,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_READ'),
    resellers: any(),
  },
  [PATHS.TARIFF_CARD_DETAIL_EDIT]: {
    component: C.TariffDetailEdit,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
    resellers: any(),
  },
  [PATHS.TARIFF_CARD_PRICING_DETAIL_EDIT]: {
    component: C.TariffPricingDetailEdit,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
    resellers: any(),
  },
  [PATHS.TARIFF_CARD_DETAIL_NEW]: {
    component: C.TariffDetailEdit,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
    resellers: any(),
  },
  [PATHS.TARIFF_CARD_PRICING_DETAIL_NEW]: {
    component: C.TariffPricingDetailEdit,
    roles: any(),
    permissions: and<EPermission>('PERMISSION_TENANT_TARIFF_WRITE'),
    resellers: any(),
  },
};

export type TRoutesOptions = Record<ValueOf<typeof PATHS>, ValueOf<typeof C>>;

export const PUBLIC_ROUTES: TRoutesOptions = {
  [PATHS.SIGN_IN]: C.SignIn,
  [PATHS.SIGN_UP]: C.SignUp,
  [PATHS.SIGN_UP_FROM_AGENT]: C.SignUp,
  [PATHS.PASSWORD_SAVED]: C.PasswordSaved,
  [PATHS.SIGN_IN_TENANT_BY_INVITE]: C.SignInByInviteLink,
  [PATHS.SIGN_IN_USER_BY_INVITE]: C.SignInByInviteLink,
  [PATHS.EMAIL_CONFIRM]: C.EmailConfirm,
  [PATHS.UNAVAILABLE]: C.ServiceUnavailable,
  [PATHS.PUBLIC_WHATSAPP_REGISTRATION]: C.WhatsAppPublicChannelForm,
  [PATHS.PUBLIC_WHATSAPP_BUSINESS_APP_REGISTRATION]: C.WhatsAppPublicChannelForm,
  [PATHS.PUBLIC_MAX_REGISTRATION]: C.MaxPublicChannelForm,
  [PATHS.PUBLIC_TELEGRAM_REGISTRATION]: C.TelegramPublicChannelForm,
};

export const getPrivatePaths = (
  userPermissions: EPermission[],
  userRoles: ERoles[],
  resellerId: EResellerIds,
) =>
  Object.entries(PRIVATE_ROUTES).reduce<string[]>(
    (acc, [path, {permissions, roles, resellers}]) => {
      const hasPermissions = permissions(userPermissions);
      const hasRoles = roles(userRoles);
      const hasReseller = resellers(resellerId);

      if (hasPermissions && hasRoles && hasReseller) {
        return [...acc, path];
      }

      return acc;
    },
    [],
  );
